// apps/client/src/hooks/pipelineTracker/useSnapshotUpload.ts
//
// Pipeline Tracker: submit a weekly export, and list what has been submitted.
//
// The ingestion callable has been working and stage-verified against real
// BigQuery since the backend landed. Nothing reached it, because the Upload tab
// was a placeholder reading "Landing next". This hook is the missing wire.
//
// NO FILE CONTENT IS RETAINED. The buffer is read, encoded, sent, and dropped.
// Upload history is audit metadata only: the server stores no cell value and no
// raw file, so there is nothing in these payloads to redact.

import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  INGESTION_CALLABLE,
  SUBMIT_SNAPSHOT_ACTION,
  LIST_UPLOADS_ACTION,
  UploadableReportType,
} from './reportContract';
import { invalidateTrackerReports } from './reportCache';

export {
  REPORT_DESTINATION,
  UPLOADABLE_REPORT_TYPES,
  REPORT_LIMITS,
  REFUSAL_GUIDANCE,
  ACCEPTED_EXTENSIONS,
  hasAcceptedExtension,
  encodedCeilingFor,
} from './reportContract';

/**
 * The Firebase callable client gives up after 70 seconds by default, while the
 * ingestion function is allowed 300. A real Task Summary (26,000 rows) takes
 * longer than 70 seconds to merge into BigQuery, so the browser reported
 * deadline-exceeded while the server was still committing (Evoke, 3 Sep 2026).
 * Match the server so the operator sees the real outcome.
 */
const CALLABLE_TIMEOUT_MS = 300_000;
export type { UploadableReportType } from './reportContract';

/**
 * Honest states only.
 *
 * There is no byte-level progress to report: the callable takes the whole file
 * in one request, so a percentage would be invented. These name what is
 * actually happening instead.
 */
export type UploadPhase = 'idle' | 'reading' | 'validating' | 'submitting' | 'processing' | 'completed' | 'failed';

export interface UploadRefusal {
  refusalCode: string | null;
  headers: string[];
  message: string;
  retryable: boolean;
}

export interface UploadOutcome {
  uploadId: string;
  outcome: string;
  parsedRowCount: number;
  committedRowCount: number;
  countsVersion?: 2 | null;
  rejectedIssueCount?: number | null;
  rejectedRowCount: number;
  warnings: number;
  ambiguousDateColumns?: string[];
}

export interface UploadHistoryRow {
  uploadId: string;
  asAtDate: string | null;
  reportType: string | null;
  state: string | null;
  parsedRowCount: number | null;
  committedRowCount: number | null;
  countsVersion?: 2 | null;
  rejectedIssueCount?: number | null;
  rejectedRowCount: number | null;
  refusedHeaders?: string[] | null;
  failureCode?: string | null;
  uploadedBy?: string | null;
  /** Present from the attribution change; absent on legacy manifests. */
  uploadedByEmail?: string | null;
  uploaderRole?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Firestore timestamps arrive in several shapes depending on the path taken. */
const toIso = (v: unknown): string | null => {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const o = v as { _seconds?: number; seconds?: number; toDate?: () => Date };
    if (typeof o.toDate === 'function') return o.toDate().toISOString();
    const secs = o._seconds ?? o.seconds;
    if (typeof secs === 'number') return new Date(secs * 1000).toISOString();
  }
  return null;
};

/**
 * Base64 without blowing the call stack.
 *
 * `String.fromCharCode(...bytes)` on a multi-megabyte export throws
 * "Maximum call stack size exceeded", which reads like a bug in the file
 * rather than in the encoder, so it is chunked.
 */
function toBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function refusalFrom(err: unknown): UploadRefusal {
  const e = err as {
    message?: string;
    details?: { refusalCode?: string; headers?: string[]; retryable?: boolean };
    code?: string;
  };
  const details = e?.details;
  return {
    refusalCode: details?.refusalCode ?? null,
    headers: Array.isArray(details?.headers) ? details!.headers! : [],
    message: e?.message ?? 'That upload could not be completed.',
    // A resource-exhausted response is worth retrying tomorrow; a refusal is
    // not worth retrying with the same file at all.
    retryable: details?.retryable ?? e?.code === 'functions/resource-exhausted',
  };
}

export function useSnapshotUpload(companyId: string | undefined) {
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [refusal, setRefusal] = useState<UploadRefusal | null>(null);
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null);
  const [history, setHistory] = useState<UploadHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Guards a state update after the tab has been navigated away from, which
  // React reports as a leak and which would otherwise resurrect a stale result.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const refreshHistory = useCallback(async () => {
    if (!companyId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const call = httpsCallable(functions, INGESTION_CALLABLE);
      const res = (await call({
        action: LIST_UPLOADS_ACTION,
        companyId,
        limit: 20,
      })) as HttpsCallableResult<{ uploads?: Record<string, unknown>[] }>;
      if (!alive.current) return;
      const rows = (res.data?.uploads ?? []).map((u) => ({
        ...(u as unknown as UploadHistoryRow),
        createdAt: toIso((u as Record<string, unknown>).createdAt),
        updatedAt: toIso((u as Record<string, unknown>).updatedAt),
      }));
      setHistory(rows);
    } catch (err) {
      if (!alive.current) return;
      // Surfaced rather than swallowed: a silent failure here looks identical
      // to "you have never uploaded anything".
      console.error('[PipelineTracker] listUploads failed', err);
      setHistoryError('Upload history could not be loaded.');
    } finally {
      if (alive.current) setHistoryLoading(false);
    }
  }, [companyId]);

  useEffect(() => { void refreshHistory(); }, [refreshHistory]);

  const submit = useCallback(async (
    file: File,
    reportType: UploadableReportType,
    asAtDate: string,
  ): Promise<boolean> => {
    if (!companyId) return false;
    setRefusal(null);
    setOutcome(null);
    try {
      setPhase('reading');
      const bytes = new Uint8Array(await file.arrayBuffer());

      setPhase('validating');
      const fileBase64 = toBase64(bytes);

      setPhase('submitting');
      const call = httpsCallable(functions, INGESTION_CALLABLE, { timeout: CALLABLE_TIMEOUT_MS });
      const pending = call({
        action: SUBMIT_SNAPSHOT_ACTION,
        companyId,
        reportType,
        asAtDate,
        fileName: file.name,
        fileBase64,
      });

      // The server parses, projects, writes and publishes inside this one call.
      setPhase('processing');
      const res = (await pending) as HttpsCallableResult<UploadOutcome>;
      if (!alive.current) return false;

      // A published snapshot changes what every report reads: drop the cached answers.
      invalidateTrackerReports();
      setOutcome(res.data);
      setPhase('completed');
      await refreshHistory();
      return true;
    } catch (err) {
      if (!alive.current) return false;
      setRefusal(refusalFrom(err));
      setPhase('failed');
      // History still refreshes: a refusal is recorded as a manifest, and
      // seeing it land is how the operator knows the attempt was registered.
      await refreshHistory();
      return false;
    }
  }, [companyId, refreshHistory]);

  const reset = useCallback(() => {
    setPhase('idle');
    setRefusal(null);
    setOutcome(null);
  }, []);

  return { phase, refusal, outcome, history, historyLoading, historyError, submit, refreshHistory, reset };
}
